import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Global default: every axios call (including ad-hoc `axios.create()` instances
// scattered across components) sends httpOnly cookies. The backend uses the
// `tgm_customer_jwt` / `tgm_vendor_jwt` / `tgm_admin_jwt` cookies for auth and
// also keeps Authorization Bearer header support during the localStorage→cookie
// migration period so legacy sessions remain valid until their JWT expires.
axios.defaults.withCredentials = true;

const client = axios.create({
  baseURL: API,
  timeout: 20000,
  // Send httpOnly cookies (tgm_customer_jwt) for all customer-authenticated requests.
  // The backend ALSO accepts an Authorization Bearer header during the localStorage→cookie
  // migration period, so existing sessions are not disrupted.
  withCredentials: true,
});

// Legacy localStorage token fallback (transitional — new logins no longer write here).
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('tgmart_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const api = {
  // Categories
  getCategoriesTree: () => client.get('/categories/tree').then((r) => r.data),
  getMainCategories: () => client.get('/main-categories').then((r) => r.data),
  getMainCategory: (key) => client.get(`/main-categories/${key}`).then((r) => r.data),
  getCategories: (mainId) => client.get('/categories', { params: { main_id: mainId } }).then((r) => r.data),
  getCategory: (key) => client.get(`/categories/${key}`).then((r) => r.data),
  getSubCategories: (categoryId) => client.get('/sub-categories', { params: { category_id: categoryId } }).then((r) => r.data),
  getSubCategory: (key) => client.get(`/sub-categories/${key}`).then((r) => r.data),

  // Products
  getProducts: (params = {}) => client.get('/products', { params }).then((r) => r.data),
  getProduct: (key) => client.get(`/products/${key}`).then((r) => r.data),

  // Suppliers
  getSuppliers: (params = {}) => client.get('/suppliers', { params }).then((r) => r.data),
  getSupplier: (key) => client.get(`/suppliers/${key}`).then((r) => r.data),

  // Misc
  getTrendingSearches: () => client.get('/trending-searches').then((r) => r.data),
  getStats: () => client.get('/stats').then((r) => r.data),
  getBrands: () => client.get('/brands').then((r) => r.data),
  searchSuggest: (q) => client.get('/search/suggest', { params: { q } }).then((r) => r.data),

  // Write
  createEnquiry: (payload) => client.post('/enquiries', payload).then((r) => r.data),
  createRequirement: (payload) => client.post('/requirements', payload).then((r) => r.data),

  // Auth
  sendOtp: (mobile) => client.post('/auth/send-otp', { mobile }).then((r) => r.data),
  deleteAccount: ({ mobile, password, reason }) => client.post('/account/delete', { mobile, password, reason }).then((r) => r.data),
  getFooterSettings: () => client.get('/footer-settings').then((r) => r.data),
  verifyOtp: (mobile, otp) => client.post('/auth/verify-otp', { mobile, otp }).then((r) => r.data),
  me: () => client.get('/auth/me').then((r) => r.data),
  logout: () => client.post('/auth/logout').then((r) => r.data),
  vendorLogout: () => client.post('/vendor/auth/logout').then((r) => r.data),

  // Branding & SEO (public)
  getBranding: () => client.get('/branding').then((r) => r.data),
  getHeroSettings: () => client.get('/hero-settings').then((r) => r.data),
  getFooterSettings: () => client.get('/footer-settings').then((r) => r.data),
  getSeoHome: () => client.get('/seo/home').then((r) => r.data),
  getNavSettings: () => client.get('/nav-settings').then((r) => r.data),
  getPwaManifest: () => client.get('/manifest.json').then((r) => r.data),

  // Cities (public)
  getCities: () => client.get('/cities').then((r) => r.data),
  getDistricts: () => client.get('/districts').then((r) => r.data),

  // Seller Types (public — admin manages full list, public list filters out verified_only entries)
  getSellerTypes: (opts = {}) =>
    client.get('/seller-types', { params: opts.includeVerifiedOnly ? { include_verified_only: true } : {} })
      .then((r) => r.data),

  // Business Types (public — admin-managed catalogue)
  getBusinessTypes: () => client.get('/business-types').then((r) => r.data),

  // Vendor public certificates (only admin-verified)
  getSupplierCertificates: (id) => client.get(`/suppliers/${id}/certificates`).then((r) => r.data),

  // Public vendor mobile reveal (captcha-gated for anonymous, free for logged-in customers)
  getCaptchaMath: () => client.get('/captcha/math').then((r) => r.data),
  revealVendorMobile: (vendorId, payload = {}) =>
    client.post(`/suppliers/${vendorId}/reveal-mobile`, payload).then((r) => r.data),

  // Vendor Claim flow (public)
  claimSendOtp: (vendorId) => client.post(`/vendors/${vendorId}/claim/send-otp`).then((r) => r.data),
  claimVerifyOtp: (vendorId, otp) => client.post(`/vendors/${vendorId}/claim/verify`, { mobile: '', otp }).then((r) => r.data),
};

export const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
